import React from 'react';
import HomeView from '../views/home/HomeView';
import ServicesView from '../views/services/ServicesView';
import IndustryView from '../views/industry/IndustryView';
import Error500 from '../views/error/Error500';
import Error404 from '../views/error/Error404';
import {getPath} from '@epic-core/common';
import {Search} from '../views/search/SearchWrapper';
import BlankView from '../views/blank/BlankView';
import NewsView from '../views/news/NewsView';
import NewsDetailView from '../views/news/NewsDetailView';
import FAQView from '../views/faq/FAQView';
import LicensingView from '../views/licensing/LicensingView';
import {RouteMatchOrUrlParse} from 'epic-ue-shared';
import {RouteObject, useRoutes} from 'react-router';
import {getAppRedirects} from './appRedirects';
import SdkView from '../views/sdk/SdkView';

export type GetInitialPRops = (match: RouteMatchOrUrlParse) => Promise<any> | ((match: RouteMatchOrUrlParse) => Promise<any>) | (() => Promise<any>);

export interface ServerRoute extends RouteObject {
    getInitialProps?: GetInitialPRops;
}

export interface ConnectedPage {
    (args?: any): JSX.Element;
    getInitialProps?: GetInitialPRops;
}

/**
 * All app routes are defined here and have an additional property for determining
 * what data to load on the server and init into shared values
 */
export const getServerRoutes = (): ServerRoute[] => {
    const path = getPath();

    const redirects = getAppRedirects();

    const routes = redirects.concat([
        {
            path: `${path}:locale/home`,
            getInitialProps: HomeView.getInitialProps,
            element: <HomeView />
        },
        {
            path: `${path}:locale/services`,
            getInitialProps: ServicesView.getInitialProps,
            element: <ServicesView />
        },
        {
            path: `${path}:locale/services-crossplay`,
            getInitialProps: ServicesView.getInitialProps,
            element: <ServicesView />
        },
        {
            path: `${path}:locale/services-games`,
            getInitialProps: ServicesView.getInitialProps,
            element: <ServicesView />
        },
        {
            path: `${path}:locale/services-account`,
            getInitialProps: ServicesView.getInitialProps,
            element: <ServicesView />
        },
        {
            path: `${path}:locale/services-social`,
            getInitialProps: ServicesView.getInitialProps,
            element: <ServicesView />
        },
        {
            path: `${path}:locale/faq`,
            getInitialProps: FAQView.getInitialProps,
            element: <FAQView />
        },
        {
            path: `${path}:locale/licensing`,
            getInitialProps: LicensingView.getInitialProps,
            element: <LicensingView />
        },
        {
            path: `${path}:locale/sdk`,
            getInitialProps: SdkView.getInitialProps,
            element: <SdkView />
        },
        {
            path: `${path}:locale/services/terms/trust-statement`,
            getInitialProps: IndustryView.getInitialProps,
            element: <IndustryView />
        },
        {
            path: `${path}:locale/search`,
            getInitialProps: Search.getInitialProps,
            element: <Search />
        },
        {
            path: `${path}:locale/news`,
            getInitialProps: NewsView.getInitialProps,
            element: <NewsView />
        },
        {
            path: `${path}:locale/news/:title`,
            getInitialProps: NewsDetailView.getInitialProps,
            element: <NewsDetailView />
        },
        {
            path: `${path}:locale/page-not-found`,
            element: <Error404 />
        },
        {
            path: `${path}:locale/error-404`,
            element: <Error404 />
        },
        {
            path: `${path}:locale/unexpectedError`,
            element: <Error500 />
        },
        {
            path: `${path}:locale/error-500`,
            element: <Error500 />
        },
        {
            path: `${path}:locale/*`,
            getInitialProps: BlankView.getInitialProps,
            element: <BlankView />
        }
    ]);

    return routes;
};

/**
 * Returns routes in an array that react-router expects, dropping the extra fields used for the server render
 */
export const getRoutes = (): RouteObject[] => {
    const serverRoutes = getServerRoutes();
    const routes = serverRoutes.map(route => {
        return {
            path: route.path,
            element: route.element
        };
    });

    return routes;
};

export const useRoutesElement = (): React.ReactElement | null => {
    return useRoutes(getRoutes());
};

export default getRoutes;
